import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/layoutChrome";
import { ModuleFeatureCard } from "@/components/HubModuleCard";
import ModulePlanBlockedDialog from "@/components/ModulePlanBlockedDialog";
import ClientWorkPickerDialog from "@/components/ClientWorkPickerDialog";
import { WORK_MODULES } from "@/modules/workModuleCore";
import { resolveModuleNavIcon } from "@/modules/moduleNavIcons";
import { useModuleAccess } from "@/modules/useModuleAccess";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { useUiPreferences } from "@/lib/useUiPreferences";
import { LayoutDashboard } from "lucide-react";
import DespachoOperationalSummary from "@/components/despacho/DespachoOperationalSummary";
import api from "@/lib/api";
import { CLIENT_MODULE_SCOPES } from "@/lib/clientModuleScope";
import { getStoredClientId, setStoredClientId } from "@/lib/clientSelection";

/** Rutas de hub que no exigen cliente de trabajo al entrar. */
const PATHS_SKIP_CLIENT_GATE = new Set([
  "/clientes",
  "/datos-maestros",
  "/ayuda",
  "/gestion-datos",
  "/exportacion-local",
  "/despacho/auxiliares",
  "/despacho/inicio",
  "/laboral/inicio",
  "/tramites/inicio",
  "/disenador/inicio",
  "/visor/inicio",
  "/materialidad/inicio",
  "/materialidad/expediente",
]);

/** Módulos de trabajo general (todas las empresas): sin aviso de seleccionar cliente.
 * Visor y Asistente Laboral SÍ exigen cliente al abrir una tarjeta/paso. */
const MODULES_SKIP_CLIENT_GATE = new Set([
  WORK_MODULES.GESTION_DESPACHO,
  WORK_MODULES.MATERIALIDAD,
]);

function clientScopeForModule(moduleId) {
  if (moduleId === WORK_MODULES.ASISTENTE_LABORAL) return CLIENT_MODULE_SCOPES.ASISTENTE_LABORAL;
  if (moduleId === WORK_MODULES.TRAMITES_FISCALES) return CLIENT_MODULE_SCOPES.TRAMITES;
  return CLIENT_MODULE_SCOPES.VISOR_SAT;
}

function moduleNeedsClientGate(moduleId) {
  return !MODULES_SKIP_CLIENT_GATE.has(moduleId);
}

function pathNeedsClient(path) {
  const base = String(path || "").split("?")[0];
  if (!base || PATHS_SKIP_CLIENT_GATE.has(base)) return false;
  return true;
}

/**
 * Vista de inicio de módulo — tarjetas derivadas del registro de pestañas (VISOR_TABS, etc.).
 * Puerta de entrada opcional; el sidebar mantiene deep links directos.
 *
 * @param {object} props
 * @param {string} props.moduleId - WORK_MODULES.VISOR_SAT | ASISTENTE_LABORAL
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {() => Array} props.getHubCards - Factory inyectada desde modulo_*.js (evita ciclo con main_ui)
 */
export default function ModuleHubInicio({ moduleId, title, subtitle, getHubCards, children }) {
  const navigate = useNavigate();
  const isPlatformDesigner = usePlatformDesigner();
  const { buyerModuleSidebarEnabled } = useUiPreferences();
  const clientScope = clientScopeForModule(moduleId);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(() => getStoredClientId(clientScope));
  const [clientGate, setClientGate] = useState({ open: false, path: "", label: "" });

  const cards = useMemo(() => {
    try {
      const raw = getHubCards?.() || [];
      if (isPlatformDesigner) return raw;
      return raw.filter((card) => !card.designerOnly);
    } catch {
      return [];
    }
  }, [getHubCards, isPlatformDesigner]);

  const accessModuleId = moduleId || WORK_MODULES.VISOR_SAT;

  const {
    hasModuleAccess,
    accessLoading,
    guardCriticalAction,
    planBlockedOpen,
    setPlanBlockedOpen,
  } = useModuleAccess(accessModuleId);

  useEffect(() => {
    setClientId(getStoredClientId(clientScope));
  }, [clientScope]);

  useEffect(() => {
    api.get("/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    const sync = () => setClientId(getStoredClientId(clientScope));
    const onChanged = (e) => {
      if (e?.detail?.scope && e.detail.scope !== clientScope) return;
      setClientId(e?.detail?.clientId ?? getStoredClientId(clientScope));
    };
    window.addEventListener("storage", sync);
    window.addEventListener("visor:client-changed", onChanged);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("visor:client-changed", onChanged);
    };
  }, [clientScope]);

  const grouped = useMemo(() => {
    const sections = [];
    const byKey = new Map();
    for (const card of cards) {
      const key = card.navSection || "";
      if (!byKey.has(key)) {
        byKey.set(key, []);
        sections.push({ key, label: key || null, items: byKey.get(key) });
      }
      byKey.get(key).push(card);
    }
    return sections;
  }, [cards]);

  const goToFeature = useCallback((path) => {
    guardCriticalAction(() => navigate(path));
  }, [guardCriticalAction, navigate]);

  const openFeature = (path, label = "") => {
    const needsClient = moduleNeedsClientGate(accessModuleId) && pathNeedsClient(path);
    const hasClient = Boolean(String(clientId || "").trim());
    if (needsClient && !hasClient) {
      setClientGate({ open: true, path, label: label || "" });
      return;
    }
    goToFeature(path);
  };

  const handleClientSelect = (id) => {
    setStoredClientId(id, clientScope);
    setClientId(id);
    const pending = clientGate.path;
    setClientGate({ open: false, path: "", label: "" });
    if (pending) goToFeature(pending);
  };

  return (
    <>
      <PageHeader
        title={title || "Inicio del módulo"}
        subtitle={
          subtitle
          || (buyerModuleSidebarEnabled
            ? "Acceso rápido por tarjetas o use el menú lateral para ir directo a cada pantalla."
            : "Acceso rápido por tarjetas. Use «Mostrar menú» en la barra superior para activar el menú lateral.")
        }
        breadcrumb={[title || "Módulo", "Inicio"]}
      />

      <div className="p-8 w-full max-w-none space-y-8" data-testid={`module-hub-inicio-${moduleId}`}>
        {grouped.map(({ key, label, items }) => (
          <section key={key || "__default__"} aria-label={label || "Funciones del módulo"}>
            {label ? (
              <h2 className="text-xs font-bold uppercase tracking-wider text-[#0F2D5C] mb-3">
                {label}
              </h2>
            ) : null}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
              {items.map((card) => {
                const Icon = resolveModuleNavIcon(card.iconKey, LayoutDashboard);
                return (
                  <ModuleFeatureCard
                    key={card.path}
                    label={card.label}
                    description={card.description}
                    icon={Icon}
                    hasModuleAccess={hasModuleAccess}
                    accessLoading={accessLoading}
                    navStep={card.navStep}
                    pending={card.pending}
                    testId={card.testid}
                    cardCategory={card.category}
                    cardStatus={card.status}
                    onEnter={() => openFeature(card.path, card.label)}
                  />
                );
              })}
            </div>
          </section>
        ))}

        {cards.length === 0 ? (
          <p className="text-sm text-slate-600">No hay funciones registradas para este módulo.</p>
        ) : null}

        <DespachoOperationalSummary />

        {children}
      </div>

      <ModulePlanBlockedDialog open={planBlockedOpen} onOpenChange={setPlanBlockedOpen} />
      {moduleNeedsClientGate(accessModuleId) ? (
        <ClientWorkPickerDialog
          open={clientGate.open}
          onOpenChange={(open) => {
            if (!open) setClientGate({ open: false, path: "", label: "" });
            else setClientGate((prev) => ({ ...prev, open: true }));
          }}
          clients={clients}
          clientId={clientId}
          onSelect={handleClientSelect}
          mode="require"
          pendingLabel={clientGate.label}
        />
      ) : null}
    </>
  );
}
